import { MergeIntegrationPassthroughRequest } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import { MergeIntegrationPassthroughRequestCallType } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import { MergeIntegrationPassthroughRequestCallTypeValue } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import { MergeIntegrationPassthroughRequestCallTypeValues } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import React, { useCallback, useMemo } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const DataSetMergeCallTypeComponent = ({
  callType,
  setCallType,
}: {
  callType: MergeIntegrationPassthroughRequestCallType;
  setCallType: Setter<MergeIntegrationPassthroughRequestCallType>;
}) => {
  const language = useLanguage();

  const listItems: ListItem<MergeIntegrationPassthroughRequestCallTypeValue>[] =
    useMemo(() => {
      return MergeIntegrationPassthroughRequestCallTypeValues.map((value) => ({
        optionLabel: value,
        optionValue: value,
      }));
    }, []);

  const typeSpecificUi = useMemo(() => {
    if (callType.callType === 'single') {
      return <div></div>;
    } else if (callType.callType === 'groupByEntity') {
      return (
        <input
          className="form-input"
          type="text"
          value={callType.entity}
          onChange={(e) => {
            setCallType({
              callType: 'groupByEntity',
              entity: e.target.value,
            });
          }}
        />
      );
    }
  }, [callType, setCallType]);

  const setCallTypeValue = useCallback(
    (callType: MergeIntegrationPassthroughRequestCallTypeValue) => {
      if (callType === 'single') {
        setCallType({
          callType: 'single',
        });
      } else if (callType === 'groupByEntity') {
        setCallType({
          callType: 'groupByEntity',
          entity: '',
        });
      } else {
        throw guardExhaustive(callType);
      }
    },
    [setCallType],
  );
  return (
    <Spacing size={'sm'} direction={'vertical'}>
      <SelectBoxWithSearch
        type={'single'}
        label={'Type'}
        list={listItems}
        selected={callType.callType}
        setSelected={(selected) => {
          if (typeof selected === 'function' || selected === null) {
            return;
          }

          setCallTypeValue(selected);
        }}
      />
      {typeSpecificUi}
    </Spacing>
  );
};

export const DataSetMergePassthroughRequestsComponent = ({
  passthroughRequests,
  setPassthroughRequests,
}: {
  passthroughRequests: MergeIntegrationPassthroughRequest[];
  setPassthroughRequests: Setter<MergeIntegrationPassthroughRequest[]>;
}) => {
  const language = useLanguage();

  const addSingle = useCallback(() => {
    setPassthroughRequests([
      ...passthroughRequests,
      {
        acceptErrors: false,
        filename: '',
        url: '',
        callType: {
          callType: 'single',
        },
      },
    ]);
  }, [passthroughRequests, setPassthroughRequests]);

  const addGroupBy = useCallback(() => {
    setPassthroughRequests([
      ...passthroughRequests,
      {
        acceptErrors: false,
        filename: '',
        url: '',
        callType: {
          callType: 'groupByEntity',
          entity: '',
        },
      },
    ]);
  }, [passthroughRequests, setPassthroughRequests]);

  const setPassthroughRequest = useCallback(
    (
      index: number,
      passthroughRequest: Partial<MergeIntegrationPassthroughRequest>,
    ) => {
      setPassthroughRequests([
        ...passthroughRequests.map((prevItem, prevIndex) =>
          prevIndex === index
            ? { ...prevItem, ...passthroughRequest }
            : prevItem,
        ),
      ]);
    },
    [passthroughRequests, setPassthroughRequests],
  );

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <S2Typography
        variant={'headline-5'}
        translatedContent={language.dangerousUserInput({
          value: 'Passthrough Requests',
        })}
      />
      <Spacing size={'lg'} direction={'horizontal'}>
        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              addSingle();
            },
          }}
        >
          <div>Add Single</div>
        </Button>

        <Button
          behavior={{
            type: 'onClick',
            onClick: () => {
              addGroupBy();
            },
          }}
        >
          <div>Add Group By</div>
        </Button>
      </Spacing>

      <Table
        items={passthroughRequests}
        columnDefinitions={[
          {
            id: 'id',
            header: () => <div>ID</div>,
            cell: ({ rowIndex }) => <div>{rowIndex + 1}</div>,
          },
          {
            id: 'acceptErrors',
            header: () => <div>Accept Errors?</div>,
            cell: ({ rowIndex, item }) => (
              <S2Checkbox
                checked={item.acceptErrors}
                onChange={() => {
                  setPassthroughRequest(rowIndex, {
                    acceptErrors: !item.acceptErrors,
                  });
                }}
              >
                <div></div>
              </S2Checkbox>
            ),
          },
          {
            id: 'filename',
            header: () => <div>Output Filename</div>,
            cell: ({ rowIndex, item }) => (
              <input
                type="text"
                className={'form-input w-full'}
                value={item.filename}
                onChange={(e) => {
                  setPassthroughRequest(rowIndex, {
                    filename: e.target.value,
                  });
                }}
              />
            ),
          },
          {
            id: 'url',
            header: () => <div>URL</div>,
            cell: ({ rowIndex, item }) => (
              <input
                type="text"
                className={'form-input w-full'}
                value={item.url}
                onChange={(e) => {
                  setPassthroughRequest(rowIndex, {
                    url: e.target.value,
                  });
                }}
              />
            ),
          },
          {
            id: 'callType',
            header: () => <div>Call Type</div>,
            cell: ({ rowIndex, item }) => (
              <DataSetMergeCallTypeComponent
                callType={item.callType}
                setCallType={(callType) => {
                  if (typeof callType === 'function' || callType === null) {
                    return;
                  }
                  setPassthroughRequest(rowIndex, {
                    callType,
                  });
                }}
              />
            ),
          },
        ]}
      />
    </Spacing>
  );
};
